/**
 * useForecast — fetches GET /api/v1/dashboard/forecast.
 *
 * Returns six predictions:
 *   • revenue.endOfToday   — projected EoD revenue + confidence band
 *   • revenue.endOfWeek    — projected end-of-week revenue
 *   • revenue.endOfMonth   — projected end-of-month revenue
 *   • topCreators / topFans / topBrands — projected top-5 for the month
 *
 * No mock fallback per CEO directive 2026-04-28 — null + error on failure.
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 12000; // forecast does extra SQL — slightly longer budget

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useForecast(refreshKey = 0) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const controller = new AbortController();
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const res = await withTimeout(
        fetch(`${API_BASE}/dashboard/forecast`, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS, controller,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData(json.data);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  return { data, error, loading, reload: load };
}
